import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiClock, FiStar } = FiIcons;

const SubscriptionPlanCard = ({ plan, onSelect, isSelected, showTrialBadge = true }) => {
  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  const getBillingDisplay = (cycle) => {
    switch (cycle) {
      case 'weekly': return 'week';
      case 'monthly': return 'month';
      case 'quarterly': return 'quarter';
      case 'yearly': return 'year';
      default: return cycle;
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'seo': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ppc': return 'bg-green-100 text-green-800 border-green-200';
      case 'social media': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'content': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const isPopular = plan.name.toLowerCase().includes('pro');

  return (
    <motion.div
      className={`relative bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 border-2 cursor-pointer ${
        isSelected 
          ? 'border-blue-500 ring-2 ring-blue-200' 
          : 'border-gray-200 hover:border-blue-300'
      }`}
      whileHover={{ y: -4 }}
      onClick={() => onSelect(plan)}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
            <SafeIcon icon={FiStar} className="w-3 h-3" />
            <span>Most Popular</span>
          </span>
        </div>
      )}

      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-800 mb-1">{plan.name}</h3>
          <p className="text-gray-600 text-sm mb-2">{plan.description}</p>
          
          <div className="flex items-center space-x-2 mb-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(plan.category)}`}>
              {plan.category}
            </span>
            
            {plan.trial_days > 0 && showTrialBadge && (
              <span className="px-2 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200 flex items-center space-x-1">
                <SafeIcon icon={FiClock} className="w-3 h-3" />
                <span>{plan.trial_days}-day trial</span>
              </span>
            )}
          </div>
        </div>

        {isSelected && (
          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      <div className="mb-6">
        <div className="flex items-baseline space-x-1">
          <span className="text-3xl font-bold text-gray-800">{formatCurrency(plan.price)}</span>
          <span className="text-gray-600">/ {getBillingDisplay(plan.billing_cycle)}</span>
        </div>
        
        {plan.billing_cycle === 'yearly' && (
          <p className="text-sm text-green-600 mt-1">Save 20% with annual billing</p>
        )}
      </div>

      <div className="space-y-3">
        <h4 className="font-semibold text-gray-800 text-sm">Features included:</h4>
        
        <ul className="space-y-2">
          {plan.features?.map((feature, index) => (
            <li key={index} className="flex items-center space-x-2 text-sm">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-gray-600">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {plan.trial_days > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <p className="text-xs text-blue-800 font-medium">
              🎉 Start with a {plan.trial_days}-day free trial, no credit card required!
            </p>
          </div>
        </div>
      )}

      <div className="mt-6">
        <button
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
            isSelected
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {isSelected ? 'Selected' : 'Select Plan'}
        </button>
      </div>
    </motion.div>
  );
};

export default SubscriptionPlanCard;